/**
 * Worked Examples for the admin Cheat Sheets — Jun 2026 (founder request).
 * Successor-training content: each page gets realistic, annotated examples a
 * second owner can relate to. Static training data — never touches live metrics.
 * Keys match CHEAT_SHEETS page identifiers.
 */

const CHEAT_SHEET_EXAMPLES = {
  analytics: [
    {
      label: 'Reading a healthy week',
      scenario: 'Monday review: Total users 540, Active (7d) 210, New sign-ups 25, MRR $1,430.',
      steps: [
        'Active ÷ Total ≈ 39% weekly engagement — anything above 30% is healthy for a teen wellbeing app.',
        'New sign-ups (25) vs last week (18) = +39% growth. Check "Growth & Leads" for the source.',
        'If MRR rose but sign-ups fell, existing parents upgraded plans — confirm in Revenue → Subscription Events.',
      ],
    },
    {
      label: 'Spotting a problem early',
      scenario: 'DAU chart shows 3 consecutive down days outside school holidays.',
      steps: [
        'First check Stability & Health — an outage or slow AvA responses usually explains sudden dips.',
        'Then check At-Risk Users list for a spike — churn often starts as silence.',
        'If both are clean, check whether a nudge/notification worker stopped (Security & Admin → workers).',
      ],
    },
  ],
  users: [
    {
      label: 'A parent emails: "my teen is locked out"',
      scenario: 'Search the teen\'s email in the user table → profile shows status "suspended".',
      steps: [
        'Open the profile and read the audit trail at the bottom — it names the admin and reason for suspension.',
        'If the suspension was safety-related, check Safety page first before unsuspending.',
        'Click Unsuspend → the teen can log in immediately; the action is audit-logged under your name.',
      ],
    },
    {
      label: 'Promoting a helper to sub-admin',
      scenario: 'You hire a moderator who should only see Users + Safety.',
      steps: [
        'Find their account → Change Role → sub_admin.',
        'Assign domains "users" — they will only see those pages in the sidebar.',
        'Verify by asking them to log in: their Command Centre should hide Revenue and Vault entirely.',
      ],
    },
  ],
  leads: [
    {
      label: 'A warm school lead arrives',
      scenario: 'Lead card: "Deputy Head, Riverside Academy — downloaded Welcome Pack, visited pricing twice".',
      steps: [
        'Two pricing visits within a week = high intent. Move status to "Contacted" once you email them.',
        'Use the email templates in Leads → the follow-up cadence is pre-written.',
        'When they register a school, the lead auto-links — you\'ll see it as "Converted".',
      ],
    },
  ],
  revenue: [
    {
      label: 'Reading the Subscription Events feed',
      scenario: 'Feed shows: "Subscription activated — plus_monthly · Sarah W · 2h ago" then "200 Hearts purchased · same user · 1h ago".',
      steps: [
        'A plan purchase followed by a Hearts top-up within hours = a highly engaged family. This is your best-case funnel.',
        'MRR card above should have increased by the plan\'s monthly price — if not, refresh; the feed is real-time, MRR aggregates hourly.',
        'Every event here is also a Postmark receipt email to the parent — cross-check in Postmark if a parent claims "no receipt".',
      ],
    },
    {
      label: 'A refund request comes in',
      scenario: 'Parent emails: "charged twice for THE ELITE".',
      steps: [
        'Find both charges in Stripe Dashboard (search their email) — genuine duplicates show two payment intents minutes apart.',
        'Refund the duplicate in Stripe (never both). The webhook keeps HiBFF subscription state correct automatically.',
        'Reply with the refund reference; Stripe emails them a receipt within the hour.',
      ],
    },
  ],
  safety: [
    {
      label: 'A conversation gets flagged',
      scenario: 'Safety queue shows a red item: teen mentioned self-harm; AvA responded with the safeguarding script and the conversation was escalated.',
      steps: [
        'Open the flagged transcript — read the FULL context, not just the trigger sentence.',
        'Check whether AvA\'s safeguarding response fired (it shows inline, highlighted).',
        'Follow the escalation checklist: parent notification status is shown on the right. If it says "not sent", send it now.',
        'Mark reviewed with a note — reviews are audit-logged and reportable to your safeguarding board.',
      ],
    },
  ],
  security: [
    {
      label: 'Suspicious login pattern',
      scenario: 'Security page shows 40 failed logins on one parent account overnight.',
      steps: [
        'The auto-block engine will already have banned the source IP (check Blocked IPs list).',
        'If the pattern continues from new IPs, force a password reset on the target account.',
        'No action needed for scattered 1-2 failures — those are just forgotten passwords.',
      ],
    },
  ],
  audit: [
    {
      label: 'Who changed this user\'s role?',
      scenario: 'A teen suddenly has sub_admin access.',
      steps: [
        'Filter audit log by the teen\'s user ID + action "role_change".',
        'The entry shows the acting admin, timestamp, and before/after values.',
        'Revert the role on the Users page, then ask the acting admin why — every role change is attributable.',
      ],
    },
  ],
  content: [
    {
      label: 'Updating a challenge\'s wording',
      scenario: 'A teacher reports a typo in Challenge 7 of the Confidence journey.',
      steps: [
        'Content → Journeys → Confidence → Challenge 7 → Edit.',
        'Changes go live immediately for teens who have not started that challenge; in-progress teens keep their original text until completion.',
      ],
    },
  ],
  features: [
    {
      label: 'Safely trialling a new feature',
      scenario: 'You want the new Hearts Store visible to only 10% of teens.',
      steps: [
        'Feature Flags → hearts_store → set rollout to 10%.',
        'Watch Revenue and Safety for a week. Roll to 50%, then 100%.',
        'The kill switch (toggle off) takes effect on next page load — no deploy needed.',
      ],
    },
  ],
  notifications: [
    {
      label: 'Sending a school-holiday broadcast',
      scenario: 'Half-term starts Monday; you want teens nudged to keep streaks alive.',
      steps: [
        'Compose → audience "teens with active streak" → schedule for Monday 9am.',
        'Keep it under 140 characters — long notifications get truncated on Android.',
        'Check open-rate here 48h later; a healthy nudge lands 25-40% opens.',
      ],
    },
  ],
  schools: [
    {
      label: 'A real application arrives',
      scenario: 'Pending: "Lincoln High School — Austin ISD — Dr. Maria Chen, Principal — mchen@lincolnhigh.austinisd.org — 850 students".',
      steps: [
        'Verify the email domain matches the district (austinisd.org ✓). Applications from gmail/example domains are auto-rejected.',
        'Google the school + principal name — 30 seconds confirms legitimacy.',
        'Click Approve → the school admin gets a welcome email with dashboard access; students can then join with the school code.',
        'The application email you received links straight back to this page.',
      ],
    },
  ],
  corporates: [
    {
      label: 'A corporate wellbeing enquiry',
      scenario: 'Pending: "TechCorp Ltd — HR Director — 200 employees — wants adult resilience programme".',
      steps: [
        'Corporate contracts are manual: approve here, then follow up with the B2B Contract Engine on the Command Centre.',
        'Pricing is per-seat and negotiated — see the Vault → Financials for the current rate card.',
      ],
    },
  ],
  reports: [
    {
      label: 'Board pack in 5 minutes',
      scenario: 'Board meeting tomorrow; you need the monthly numbers.',
      steps: [
        'Reports → Monthly Summary → select month → Generate.',
        'It compiles users, MRR, engagement, safety stats into one document.',
        'Pair it with the Board Deck card (Founder & Board section) for the narrative slides.',
      ],
    },
  ],
  team: [
    {
      label: 'Onboarding a new team member',
      scenario: 'New safeguarding officer joins Monday.',
      steps: [
        'Team → Invite → their work email → role sub_admin → domain "safety".',
        'They receive a set-password email; their access is limited to Safety + Users.',
        'Their actions appear in the audit log from day one.',
      ],
    },
  ],
  vault: [
    {
      label: 'Preparing for an investor meeting',
      scenario: 'A VC asks for "your latest numbers" before a call.',
      steps: [
        'Vault (TOTP unlock) → create an Investor Data Room → toggle which documents they see.',
        'Generate their 6-digit passcode and send the /dataroom link — they see live metrics, not a stale PDF.',
        'Rotate the passcode after the raise closes; access dies instantly.',
      ],
    },
    {
      label: 'Live metrics sanity check',
      scenario: 'Before any investor demo, confirm the Vault numbers match the Command Centre.',
      steps: [
        'Vault MRR and Command Centre MRR come from the same canonical calculator (services/revenue.py) — they must match to the penny.',
        'If they ever diverge, do not demo — report it as a bug immediately.',
      ],
    },
  ],
  settings: [
    {
      label: 'Changing the support email',
      scenario: 'You switch from support@ to hello@hibff.com.',
      steps: [
        'Settings → Platform → support email → save.',
        'This updates the address shown in app footers and transactional email reply-to fields.',
        'Send yourself a test email (Notifications → test send) to confirm.',
      ],
    },
  ],
  'developer-support': [
    {
      label: 'A page starts erroring',
      scenario: 'Two parents report the dashboard is blank.',
      steps: [
        'Developer Support → recent frontend errors — blank pages almost always show a matching JS error here.',
        'Copy the error and timestamp into a message to your developer/agent — that single line usually pinpoints the fix.',
      ],
    },
  ],
  logs: [
    {
      label: 'Tracing one user\'s bad afternoon',
      scenario: 'A parent says "the app broke at 4pm".',
      steps: [
        'Filter logs by their user ID, 15:45–16:15 window.',
        'Look for red (5xx) entries — the request path tells you which feature failed.',
        'No red entries = the problem was on their device/network, not the platform.',
      ],
    },
  ],
  'corporate-dashboard': [
    {
      label: 'Monthly corporate check-in',
      scenario: 'TechCorp\'s HR director asks how their employees are doing.',
      steps: [
        'Their dashboard shows aggregate (never individual) wellbeing engagement.',
        'Export the monthly summary — it\'s pre-anonymised and safe to share.',
      ],
    },
  ],
  'school-dashboard': [
    {
      label: 'A teacher\'s weekly review',
      scenario: 'Class 9B: 24 of 30 students active, 3 flagged as disengaged.',
      steps: [
        'Disengaged = no activity for 14 days. The dashboard never shows conversation content — only engagement.',
        'Suggest the teacher runs a class challenge — group challenges re-engage ~60% of quiet students.',
      ],
    },
  ],
};

export default CHEAT_SHEET_EXAMPLES;
